import {useParams} from "react-router";
import {useDispatch, useSelector} from "react-redux";
import React, {useContext, useEffect, useRef, useState} from "react";
import {getClientById, getClientByIdIds, putClient} from "../../redux/CustomerClientsSlice";
import Grid from "@mui/material/Grid2";
import ValidatedTextField from "../../Components/Inputs/ValidatedTextField";
import {alphaNumericValidator, checkAllValid} from "../../Helpers/ValidationHelpers";
import Button from "@mui/material/Button";
import SaveIcon from "@mui/icons-material/Save";
import ClientDetailsView from "../../Components/Clients/ClientDetailsView";
import ClientAddressView from "../../Components/Clients/ClientAddressView";
import ClientContactView from "../../Components/Clients/ClientContactView";
import ClientQuotesView from "../../Components/Clients/ClientQuotesView";
import NotFound from "../../Components/Error/NotFound";
import GeneralError from "../../Components/Error/GeneralError";
import TrimObjectStrings from "../../Helpers/TrimObjectStrings";
import {IsOnboardedContext} from "../../Contexts/IsOnboardedContext";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import EditIcon from "@mui/icons-material/Edit";
import Breadcrumb from "../../Components/Navigation/Breadcrumb";
import LoadingOverlay from "../../Components/Loading/LoadingOverlay";
import RenderGoBackButton from "../../Helpers/RenderGoBackButton";
import equal from "fast-deep-equal";
import {useSnackbar} from "notistack";

const ClientDetails = () => {
    const { id } = useParams();
    const context = useContext(IsOnboardedContext);
    const clientFromRedux = useSelector(getClientByIdIds)[id];

    const [client, setClient] = useState(clientFromRedux);
    const [edit, setEdit] = useState(false);
    const [activeForm, setActiveForm] = useState();
    const [initialClient, setInitialClient] = useState();
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [clientUpdated, setClientUpdated] = useState(false);

    const { enqueueSnackbar } = useSnackbar();

    const [clientError, setClientError] = useState(null);
    const [pathMap, setPathMap] = useState({});
    const formValid = useRef({});

    const dispatch = useDispatch();

    const handleEdit = () => {
        setEdit(true);
        setInitialClient({
            ...client
        });
        setActiveForm({
            ...client
        });
        formValid.current = Object.keys(client).reduce((acc, field) => {
            acc[field] = true;
            return acc;
        }, {}); 
    }

    const handleCancelEdit = () => {
        setEdit(false);
        setActiveForm(null);
    }

    function handleSave() {
        if (equal(activeForm, initialClient)) {
            setIsSubmitting(false);
            setEdit(false);
            setInitialClient(activeForm);
            setClientUpdated(true);
            return;
        }
        
        if (checkAllValid(formValid.current)) {
            setIsSubmitting(true);
            dispatch(putClient(TrimObjectStrings(
                {
                    id: id,
                    payload: {
                        ...activeForm,
                        lastModifiedBy: context.userName,
                    }
                })
            )).unwrap()
                .then(() => {
                    setClient({
                        ...activeForm,
                        lastModifiedBy: context.userName
                    })
                    setIsSubmitting(false);
                    setEdit(false);
                    setInitialClient(activeForm);
                    setClientUpdated(true);
                    enqueueSnackbar("Client successfully updated!", {
                        variant: "success",
                        autoHideDuration: 3000,
                    });
                })
                .catch((error) => {
                    console.error('Failed to update client', error);
                    setIsSubmitting(false);
                    enqueueSnackbar("Failed to update client. Please try again.", {
                        variant: "error",
                        autoHideDuration: 3000,
                    });
                });
        } 
    }

    useEffect(() => {
        document.title = 'Loading...';
        if (clientFromRedux) {
            setPathMap({ [id]: clientFromRedux.name });
            if (!clientUpdated) {
                setClient(clientFromRedux);
            }
            document.title = `${clientFromRedux.name} - CPC`;
        } else {
            dispatch(getClientById(id))
                .unwrap()
                .then((response) => {
                    setClient(response)
                })
                .catch((error) => {
                    setClientError(error);
                    document.title = 'CPC';
                });
        }
    }, [dispatch, client, id]);
    
    if (client && !clientError) {
        return <>
        {isSubmitting && <LoadingOverlay />}
            <Breadcrumb pathMap={pathMap} />
            <Grid container
                  spacing={3}
            >
                <Grid size={{ xs: 12, sm: 6, md: 8 }} >
                    {!edit &&
                        <Typography variant="h2">{client.name}</Typography>
                    }
                    {edit &&
                        <ValidatedTextField
                            id={'name'}
                            label={"Name"}
                            validator={alphaNumericValidator}
                            onChange={isValid => (formValid.current.name = isValid)}
                            setActiveForm={setActiveForm}
                            isSubmitting={isSubmitting}
                            initialValue={client.name}
                            fieldPath={['name']}
                            infoAdornment={null}
                            removeHelperTextPadding={true}
                        />
                    }
                </Grid>
                {!edit && <>
                    <Grid size={{ xs: 12, sm: 6, md: 4 }} >
                        <Box  sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: {xs: 'left', sm: 'right'}}}
                        >
                            <Button variant="contained" className="edit-button" onClick={handleEdit} startIcon={<EditIcon />} >
                                Edit
                            </Button>
                        </Box>
                    </Grid>
                </>
                }
                <Grid size={{ xs: 12 }}>
                    <ClientDetailsView
                        client={client}
                        edit={edit}
                        setActiveForm={setActiveForm}
                        formValid={formValid}
                        isSubmitting={isSubmitting}
                    />
                </Grid>
                {edit ? 
                <>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <ClientAddressView
                            client={client}
                            edit={edit}
                            setActiveForm={setActiveForm}
                            formValid={formValid}
                            isSubmitting={isSubmitting}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <ClientContactView
                            client={client}
                            edit={edit}
                            setActiveForm={setActiveForm}
                            formValid={formValid}
                            isSubmitting={isSubmitting}
                        />
                    </Grid>
                </>
                    : <>
                        <Grid size={{ xs: 12, md: 6 }}>
                            {!edit &&
                                <ClientQuotesView quotes={client.quotes}/>
                            }
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <Grid container spacing={3}>
                                <Grid size={{ xs: 12 }}>
                                    <ClientAddressView
                                        client={client}
                                        edit={edit}
                                        setActiveForm={setActiveForm}
                                        formValid={formValid}
                                        isSubmitting={isSubmitting}
                                    />
                                </Grid>
                                <Grid size={{ xs: 12 }}>
                                    <ClientContactView
                                        client={client}
                                        edit={edit}
                                        setActiveForm={setActiveForm}
                                        formValid={formValid}
                                        isSubmitting={isSubmitting}
                                    />
                                </Grid>
                            </Grid>
                        </Grid>
                    </>
                }
                {edit && <>
                    <Grid size={{ xs: 12 }}
                          sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'left'
                          }} >
                        <Button
                            variant="contained"
                            className="save-button"
                            onClick={handleSave}
                            disabled={!checkAllValid(formValid.current) || isSubmitting}
                            startIcon={<SaveIcon/>}
                            sx={{ mr: 2 }}
                        >
                            Save
                        </Button>
                        {RenderGoBackButton(equal(initialClient, activeForm), handleCancelEdit)}
                    </Grid>
                </>
                }
            </Grid>
        </>
    } else if (clientError !== null) {
        if(clientError.status === 404) {
            return (
                <div>
                    <NotFound />
                </div>
            );
        } else {
            return (
                <div>
                    <GeneralError />
                </div>
            )
        }
    } else {
        return <LoadingOverlay />
    }
}

export default ClientDetails;